import moment, { Moment, RelativeTimeSpec } from "moment";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/ru";
import "moment/locale/zh-cn";
import dayjs, { Dayjs } from "dayjs";
import { toFixed } from "@shared/utils/numbers";
import {
  DAYS_IN_YEAR,
  HOURS_IN_DAY,
  LIFETIME_DATE,
  MINUTES_IN_HOUR,
  SECONDS_IN_DAY,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
  SECONDS_IN_MONTH,
  SECONDS_IN_WEEK,
  SECONDS_IN_YEAR,
  TIME_DURATION_SUFFIX,
  TIME_UNIT_TO_MULTIPLIER,
  TIME_UNITS_ORDER,
} from "@shared/common/constants";
import { TimeUnit } from "@shared/types";
import { getDateLocale } from "@shared/common/i18n/dateLocales";

type RelativeTimeUnits = Required<
  Omit<RelativeTimeSpec, "future" | "past" | "w" | "ww">
>;

const SHORT_RELATIVE_TIMES: Record<
  string,
  { future: string; past: string; units: RelativeTimeUnits }
> = {
  en: {
    future: "in %s",
    past: "%s ago",
    units: {
      s: "1s",
      ss: "%ds",
      m: "1m",
      mm: "%dm",
      h: "1h",
      hh: "%dh",
      d: "1d",
      dd: "%dd",
      M: "1M",
      MM: "%dM",
      y: "1y",
      yy: "%dy",
    },
  },
  es: {
    future: "en %s",
    past: "hace %s",
    units: {
      s: "1 s",
      ss: "%d s",
      m: "1 min",
      mm: "%d min",
      h: "1 h",
      hh: "%d h",
      d: "1 d",
      dd: "%d d",
      M: "1 mes",
      MM: "%d meses",
      y: "1 año",
      yy: "%d años",
    },
  },
  fr: {
    future: "dans %s",
    past: "il y a %s",
    units: {
      s: "1 s",
      ss: "%d s",
      m: "1 min",
      mm: "%d min",
      h: "1 h",
      hh: "%d h",
      d: "1 j",
      dd: "%d j",
      M: "1 mois",
      MM: "%d mois",
      y: "1 an",
      yy: "%d ans",
    },
  },
  ru: {
    future: "через %s",
    past: "%s назад",
    units: {
      s: "1 с",
      ss: "%d с",
      m: "1 мин",
      mm: "%d мин",
      h: "1 ч",
      hh: "%d ч",
      d: "1 дн.",
      dd: "%d дн.",
      M: "1 мес.",
      MM: "%d мес.",
      y: "1 г.",
      yy: "%d г.",
    },
  },
  "zh-cn": {
    future: "%s后",
    past: "%s前",
    units: {
      s: "1秒",
      ss: "%d秒",
      m: "1分钟",
      mm: "%d分钟",
      h: "1小时",
      hh: "%d小时",
      d: "1天",
      dd: "%d天",
      M: "1个月",
      MM: "%d个月",
      y: "1年",
      yy: "%d年",
    },
  },
};

export const getShortLocale = (locale: string, showAgo?: boolean) =>
  `${locale}-short${showAgo ? "-ago" : ""}`;

const defineShortLocale = (
  name: string,
  parentLocale: string,
  past: string
) => {
  if (moment.locales().includes(name)) return;
  const { future, units } = SHORT_RELATIVE_TIMES[parentLocale];
  moment.defineLocale(name, {
    parentLocale,
    relativeTime: { ...units, future, past },
  });
};

Object.entries(SHORT_RELATIVE_TIMES).forEach(([locale, { past }]) => {
  defineShortLocale(getShortLocale(locale), locale, "%s");
  defineShortLocale(getShortLocale(locale, true), locale, past);
});
moment.locale("en");

const LOCALIZED_DATE_FORMATS: Record<
  string,
  { full: string; short: string; shortWithYear: string }
> = {
  "zh-cn": {
    full: "YYYY年M月D日",
    short: "M月D日",
    shortWithYear: "YYYY年M月D日",
  },
};
const DEFAULT_LOCALIZED_DATE_FORMATS = {
  full: "D MMM YY",
  short: "DD MMM",
  shortWithYear: "DD MMM YY",
};

const LOCALIZED_LABEL_FORMATS: Record<string, Record<string, string>> = {
  "zh-cn": {
    "MMM D": "M月D日",
    "MMM D, YYYY": "YYYY年M月D日",
    "MMM D, HH:mm": "M月D日 HH:mm",
    "MMM D, YYYY, HH:mm": "YYYY年M月D日 HH:mm",
    "ddd, MMM D": "M月D日 ddd",
    "MMMM YYYY": "YYYY年M月",
    "MMM YYYY": "YYYY年M月",
    MMM: "M月",
  },
};
const DEFAULT_LOCALIZED_LABEL_FORMATS: Record<string, string> = {
  "MMM D": "D MMM",
  "MMM D, YYYY": "D MMM YYYY",
  "MMM D, HH:mm": "D MMM, HH:mm",
  "MMM D, YYYY, HH:mm": "D MMM YYYY, HH:mm",
  "ddd, MMM D": "ddd D MMM",
};

export const formatDateLabel = (date: Moment | Dayjs, format: string) => {
  const locale = getDateLocale();
  const formats =
    LOCALIZED_LABEL_FORMATS[locale] ?? DEFAULT_LOCALIZED_LABEL_FORMATS;
  const localizedFormat = locale === "en" ? format : formats[format] ?? format;
  const value = dayjs.isDayjs(date) ? moment(date.toDate()) : date.clone();
  return value.locale(locale).format(localizedFormat);
};

export const dateToMoment = (date: any, isUnix?: boolean) => {
  return isUnix ? moment.unix(date) : moment.utc(date);
};

export const dateToDayjs = (date: any) => {
  return dayjs.unix(date);
};

export const getDateFormatString = (
  includeTime = false,
  isShort = false,
  includeYear = false
) => {
  const timeFormat = includeTime ? " HH:mm" : "";
  const locale = getDateLocale();
  if (locale === "en") {
    const baseFormat = isShort
      ? `DD MMM${includeYear ? ", YY" : ""}${includeTime ? ", " : ""}`
      : "MMM Do, YY";
    return `${baseFormat}${timeFormat}`;
  }
  const formats =
    LOCALIZED_DATE_FORMATS[locale] ?? DEFAULT_LOCALIZED_DATE_FORMATS;
  if (!isShort) return `${formats.full}${timeFormat}`;
  return `${includeYear ? formats.shortWithYear : formats.short}${timeFormat}`;
};

export const formatMoment = (
  momentDate: Moment,
  includeTime?: boolean,
  isShort = false
) => {
  const format = getDateFormatString(includeTime, isShort);
  return momentDate.clone().locale(getDateLocale()).format(format);
};

export const formatDate = (
  date: any,
  includeTime?: boolean,
  isUnix?: boolean,
  isShort = false,
  includeYear = false
) => {
  const momentDate = dateToMoment(date, isUnix);
  const format = getDateFormatString(includeTime, isShort, includeYear);
  return momentDate.locale(getDateLocale()).format(format);
};

export const getFormatTime = (date: any, isUnix?: boolean) => {
  return dateToMoment(date, isUnix).locale("en").format("HH:mm");
};

export const formatDateRelative = (
  date: any,
  isUnix?: boolean,
  isShort?: boolean,
  showAgo?: boolean
) => {
  if (!date) return "N/A";
  const locale = getDateLocale();
  const momentDate = dateToMoment(date, isUnix);
  if (!isShort) return momentDate.locale(locale).fromNow();
  return momentDate.locale(getShortLocale(locale, showAgo)).fromNow();
};

export const getDayjsLifetimeDate = () => {
  return dayjs(LIFETIME_DATE, "MM-DD-YYYY");
};

export const getLifetimeDate = () => {
  return moment.utc(LIFETIME_DATE, "MM-DD-YYYY");
};

export const dateIsLifetime = (date: any, isUnix?: boolean) => {
  return (
    dateToMoment(date, isUnix).diff(getLifetimeDate(), "years") === 0 ||
    (isUnix && date > 20000000000)
  );
};

export const sortRawDates = (a?: string | number, b?: string | number) => {
  if (!a) {
    return 1;
  }
  if (!b) {
    return -1;
  }
  return moment(a).diff(moment(b));
};

export const getLiveTradeTimeDuration = (
  initialTimeStamp: number,
  liveGraphData?: any[]
) => {
  if (liveGraphData && liveGraphData.length > 0) {
    const difference =
      liveGraphData[liveGraphData?.length - 1][0] - initialTimeStamp;

    return getFormattedTimeDuration(difference);
  }
};

export const getFormattedTimeDuration = (seconds: number) => {
  let duration;
  let suffix;

  if (seconds <= SECONDS_IN_HOUR) {
    duration = seconds / SECONDS_IN_MINUTE;
    if (duration < 1) return "<1m";
    suffix = TIME_DURATION_SUFFIX.MINUTE;
  } else if (seconds <= SECONDS_IN_DAY) {
    duration = seconds / SECONDS_IN_HOUR;
    suffix = TIME_DURATION_SUFFIX.HOUR;
  } else if (seconds <= SECONDS_IN_WEEK) {
    duration = seconds / SECONDS_IN_DAY;
    suffix = TIME_DURATION_SUFFIX.DAY;
  } else if (seconds <= SECONDS_IN_MONTH) {
    duration = seconds / SECONDS_IN_WEEK;
    suffix = TIME_DURATION_SUFFIX.WEEK;
  } else if (seconds <= SECONDS_IN_YEAR) {
    duration = seconds / SECONDS_IN_MONTH;
    suffix = TIME_DURATION_SUFFIX.MONTH;
  } else {
    duration = seconds / SECONDS_IN_YEAR;
    suffix = TIME_DURATION_SUFFIX.YEAR;
  }
  return `${toFixed(duration, 0)}${suffix}`;
};

export const getDaysFromDate = (date: any, isUnix?: boolean) => {
  return Math.floor(
    moment.duration(moment().diff(dateToMoment(date, isUnix))).asDays()
  );
};

export const getUtcDate = (
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  seconds: number
) =>
  new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds)).getTime();

const getTime = (time: number) => Math.max(0, Math.floor(time));

export const getTimeLeft = (countdown: number) => {
  const days = getTime(countdown / 1000 / 60 / 60 / 24);
  const hours = getTime((countdown / 1000 / 60 / 60) % 24);
  const minutes = getTime((countdown / 1000 / 60) % 60);
  const seconds = getTime((countdown / 1000) % 60);
  return { days, hours, minutes, seconds };
};

export const getDisplayedTime = (number: number) =>
  number > 9 ? number : "0" + number;

export const getDisplayTime = (timeUnit: TimeUnit, time: number) => {
  const displayTime = Math.floor(time);
  let displayText = `${displayTime} ${timeUnit}${displayTime > 1 ? "s" : ""}`;
  const diff = time - displayTime;
  const index = TIME_UNITS_ORDER.findIndex((time) => time === timeUnit);
  if (diff > 0.05 && index < TIME_UNITS_ORDER?.length - 1) {
    const smallerTimeUnit = TIME_UNITS_ORDER[index + 1];
    const smallerTimeUnitValue = Math.floor(
      diff * TIME_UNIT_TO_MULTIPLIER[timeUnit]
    );
    displayText = displayText.concat(
      " ",
      smallerTimeUnitValue.toString(),
      ` ${smallerTimeUnit}${smallerTimeUnitValue > 1 ? "s" : ""}`
    );
  }
  return displayText;
};

export const getDisplayTimeDuration = (seconds?: number) => {
  if (!seconds) return;
  let displayText;
  const minutes = seconds / SECONDS_IN_MINUTE;
  const hours = minutes / MINUTES_IN_HOUR;
  const days = hours / HOURS_IN_DAY;
  const years = days / DAYS_IN_YEAR;
  if (years >= 1) {
    displayText = getDisplayTime(TimeUnit.YEAR, years);
  } else if (days >= 1) {
    displayText = getDisplayTime(TimeUnit.DAY, days);
  } else if (hours >= 1) {
    displayText = getDisplayTime(TimeUnit.HOUR, hours);
  } else if (minutes >= 1) {
    displayText = getDisplayTime(TimeUnit.MINUTE, minutes);
  } else {
    displayText = getDisplayTime(TimeUnit.SECOND, seconds);
  }
  return displayText;
};

export const getDaysDiff = (start?: number, end?: number) => {
  if (!start || !end) return;
  return Math.round((end - start) / (60 * 60 * 24));
};
